/**
 * The one way to read what a character is wearing (#250). Nothing reads
 * `character.equipped` itself (eslint.config.js enforces it): every stat,
 * combat and display path asks for its items as wielded, so a requirement
 * the wearer falls short of weakens them the same way everywhere. Moving
 * and saving items reads the stored (full-strength) loadout instead.
 */
import { resolveVeilAttunementPercent } from './essence-progression';
import { resolveGearPotency, resolveMartialDisciplinePercent, type CharacterDisciplines } from './gear-requirements';
import { buildEquipment } from './item-instances';
import type { CharacterEquipmentLoadout, Equipment, Skill } from './types/game';

/** One equipment slot: a hand, the body, or an accessory. */
export type EquippedSlot =
    | { readonly group: 'weapons'; readonly index: number }
    | { readonly group: 'armor' }
    | { readonly group: 'miscellaneous'; readonly index: number };

/** How many slots each equipment group has: two hands, one body, four accessories (`CharacterEquipmentLoadout`). */
export const EQUIPPED_SLOT_COUNTS = { weapons: 2, armor: 1, miscellaneous: 4 } as const satisfies {
    readonly weapons: CharacterEquipmentLoadout['weapons']['length'];
    readonly armor: 1;
    readonly miscellaneous: CharacterEquipmentLoadout['miscellaneous']['length'];
};

/** Whether `index` names a slot of `group` (armor's one slot is 0). */
export function isEquippedSlotIndex(group: EquippedSlot['group'], index: number): boolean {
    return Number.isInteger(index) && index >= 0 && index < EQUIPPED_SLOT_COUNTS[group];
}

/** What wears gear: a character's loadout, and what its requirements are judged against. */
export interface EquipmentWearer {
    readonly equipped: CharacterEquipmentLoadout;
    readonly skills?: ReadonlyArray<Pick<Skill, 'id' | 'level'>>;
    /** Veil Attunement (0-100%), where a character carries it worked out (a run's characters do). */
    readonly veilAttunementPercent?: number;
    /** Essence spent on Essence abilities, all runs: Veil Attunement, when no percent is carried. */
    readonly veilAttunementSpent?: number;
    /** An NPC's gear is never weakened. */
    readonly type?: string;
}

/** A character type whose loadout is optional (some payloads carry none). */
export type MaybeEquipmentWearer = Omit<EquipmentWearer, 'equipped'> & { readonly equipped?: CharacterEquipmentLoadout };

/** The wearer's standing in each discipline; undefined for one whose gear is never weakened (an NPC). */
export function resolveWearerDisciplines(wearer: MaybeEquipmentWearer): CharacterDisciplines | undefined {
    if (wearer.type === 'npc') {
        return undefined;
    }
    return {
        veilAttunement: wearer.veilAttunementPercent ?? resolveVeilAttunementPercent(wearer.veilAttunementSpent ?? 0),
        martialDiscipline: resolveMartialDisciplinePercent(wearer.skills ?? []),
    };
}

/** Weakened copies, by item and potency: an item object is replaced, never changed, when it moves. */
const weakenedCopies = new WeakMap<Equipment, Map<number, Equipment>>();

/** An item as its wearer wields it: itself at full strength, else a weakened copy. */
function wield(item: Equipment | null, disciplines: CharacterDisciplines | undefined): Equipment | null {
    if (!item || !disciplines || !item.instance) {
        return item;
    }
    const potency = resolveGearPotency(item, disciplines);
    if (potency >= 1) {
        return item;
    }
    const copies = weakenedCopies.get(item) ?? new Map<number, Equipment>();
    weakenedCopies.set(item, copies);
    const cached = copies.get(potency);
    if (cached) {
        return cached;
    }
    const weakened = buildEquipment(item, item.instance, undefined, potency);
    copies.set(potency, weakened);
    return weakened;
}

function storedItemAt(loadout: CharacterEquipmentLoadout, slot: EquippedSlot): Equipment | null {
    switch (slot.group) {
        case 'weapons':
            return loadout.weapons[slot.index] ?? null;
        case 'armor':
            return loadout.armor;
        case 'miscellaneous':
            return loadout.miscellaneous[slot.index] ?? null;
    }
}

/** The item in one slot, as its wearer wields it; null when empty or with no loadout. */
export function getEquippedItem(wearer: MaybeEquipmentWearer, slot: EquippedSlot): Equipment | null {
    if (!wearer.equipped) {
        return null;
    }
    return wield(storedItemAt(wearer.equipped, slot), resolveWearerDisciplines(wearer));
}

/** The whole loadout as its wearer wields it, slot for slot: for anything that turns gear into stats. Undefined for a wearer with no loadout. */
export function getWieldedLoadout(wearer: EquipmentWearer): CharacterEquipmentLoadout;
export function getWieldedLoadout(wearer: MaybeEquipmentWearer): CharacterEquipmentLoadout | undefined;
export function getWieldedLoadout(wearer: MaybeEquipmentWearer): CharacterEquipmentLoadout | undefined {
    if (!wearer.equipped) {
        return undefined;
    }
    const disciplines = resolveWearerDisciplines(wearer);
    const { weapons, armor, miscellaneous } = wearer.equipped;
    return {
        weapons: [wield(weapons[0], disciplines), wield(weapons[1], disciplines)],
        armor: wield(armor, disciplines),
        miscellaneous: [
            wield(miscellaneous[0], disciplines),
            wield(miscellaneous[1], disciplines),
            wield(miscellaneous[2], disciplines),
            wield(miscellaneous[3], disciplines),
        ],
    };
}

/** Every item worn (weapons, armor, then accessories), as its wearer wields it; none without a loadout. */
export function getEquippedItems(wearer: MaybeEquipmentWearer): Equipment[] {
    const loadout = getWieldedLoadout(wearer);
    if (!loadout) {
        return [];
    }
    const { weapons, armor, miscellaneous } = loadout;
    return [...weapons, armor, ...miscellaneous].filter((item): item is Equipment => item !== null);
}

/** The loadout as stored, every item at full strength: for moving, saving and sending items, never for stats. */
export function getStoredLoadout(wearer: Pick<EquipmentWearer, 'equipped'>): CharacterEquipmentLoadout;
export function getStoredLoadout(wearer: Pick<MaybeEquipmentWearer, 'equipped'>): CharacterEquipmentLoadout | undefined;
export function getStoredLoadout(wearer: Pick<MaybeEquipmentWearer, 'equipped'>): CharacterEquipmentLoadout | undefined {
    return wearer.equipped;
}

/** Puts an item in a slot (null empties it). */
export function setEquippedItem(wearer: Pick<EquipmentWearer, 'equipped'>, slot: EquippedSlot, item: Equipment | null): void {
    const loadout = wearer.equipped;
    switch (slot.group) {
        case 'weapons':
            loadout.weapons[slot.index] = item;
            return;
        case 'armor':
            loadout.armor = item;
            return;
        case 'miscellaneous':
            loadout.miscellaneous[slot.index] = item;
            return;
    }
}
